import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid, // Added for image grid
  Paper,
  Alert, // Added for image container
} from '@mui/material'; // Corrected Alert import
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../networking/axiosconfig'; // Import your API client
import { useSelector } from 'react-redux'; // Import useSelector to access config

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: config } = useSelector((state) => state.config);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: '',
    price: '',
    discountPercentage: '',
    description: '',
    highlights: '',
    deliveryTime: '',
    isFeatured: false,
    colors: [],
    images: [],
    stock: '',
    storeCode: 'SONO55',
    specifications: {},
    content: '',
    filters: []
  });
  
  // For display purposes, we'll keep track of brand and category names
  const [brandName, setBrandName] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newColor, setNewColor] = useState('');
  const [newImage, setNewImage] = useState('');
  const [newSpecificationKey, setNewSpecificationKey] = useState('');
  const [newSpecificationValue, setNewSpecificationValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const isEditMode = !!id; // Determine if it's edit mode based on id

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories with name field only for better performance
        const [categoriesResponse, brandsResponse] = await Promise.all([
          apiClient.get(`${config.API_BASE_URL}/api/categories`),
          apiClient.get(`${config.API_BASE_URL}/api/brands`)
        ]);
        
        setCategories(categoriesResponse.data);
        setBrands(brandsResponse.data);
        
        // If editing, set the brand and category names
        if (isEditMode) {
          const productResponse = await apiClient.get(`${config.API_BASE_URL}/api/products/${id}`);
          const productData = productResponse.data;
          
          // Correctly set form data by extracting IDs from populated fields
          setFormData({
            ...productData,
            brand: productData.brand?._id,
            category: productData.category?._id,
          });
          const { brand, category } = productData;
          
          setBrandName(brand?.name || '');
          setCategoryName(category?.name || '');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load required data. Please try again later.');
      }
    };

    fetchData();
  }, [id, config, isEditMode]);

  const handleCancel = () => {
    navigate('/products');
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === 'brand') {
      const selectedBrand = brands.find(b => b._id === value);
      setBrandName(selectedBrand?.name || '');
    }

    if (name === 'category') {
      const selectedCategoryData = categories.find(c => c._id === value);
      setCategoryName(selectedCategoryData?.name || '');
      let newFilters = [];
      if (selectedCategoryData && selectedCategoryData.filters) {
        newFilters = selectedCategoryData.filters
          .map(filter => {
            if (filter.values && filter.values.length > 0) {
              return { key: filter.key, valueId: filter.values[0]._id };
            }
            return null;
          })
          .filter(Boolean);
      }
      setFormData(prev => ({ ...prev, category: value, filters: newFilters }));
      return;
    }

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFilterChange = (filterKey, valueId) => {
    setFormData(prev => {
      const otherFilters = prev.filters.filter(f => f.key !== filterKey);
      return {
        ...prev,
        filters: [...otherFilters, { key: filterKey, valueId }]
      };
    });
  };

  const handleAddColor = () => {
    if (newColor && !formData.colors.includes(newColor)) {
      setFormData({ ...formData, colors: [...formData.colors, newColor] });
      setNewColor('');
    }
  };

  const handleDeleteColor = (colorToDelete) => {
    setFormData({
      ...formData,
      colors: formData.colors.filter((color) => color !== colorToDelete),
    });
  };

  const handleAddImage = () => {
    if (newImage && !formData.images.includes(newImage)) {
      setFormData({ ...formData, images: [...formData.images, newImage] });
      setNewImage('');
    }
  };

  const handleDeleteImage = (imageToDelete) => {
    setFormData({
      ...formData,
      images: formData.images.filter((image) => image !== imageToDelete),
    });
  };

  const handleAddSpecification = () => {
    if (newSpecificationKey && newSpecificationValue) {
      setFormData({
        ...formData,
        specifications: {
          ...formData.specifications,
          [newSpecificationKey]: newSpecificationValue,
        },
      });
      setNewSpecificationKey('');
      setNewSpecificationValue('');
    }
  };

  const handleDeleteSpecification = (keyToDelete) => {
    const { [keyToDelete]: deletedKey, ...restSpecifications } = formData.specifications;
    setFormData({ ...formData, specifications: restSpecifications });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      // Ensure config and API_BASE_URL are available
      if (!config || !config.API_BASE_URL) {
        setError('Configuration error: API base URL is not set.');
        setLoading(false);
        return;
      }
      const apiUrl = `${config.API_BASE_URL}/api/products`;
      const method = isEditMode ? 'put' : 'post';

      // Ensure brand and category are sent as IDs, not objects
      const payload = {
        ...formData,
        brand: formData.brand?._id || formData.brand,
        category: formData.category?._id || formData.category,
      };

      if (isEditMode) {
        payload.productId = id; // Add productId to formData if in edit mode
      }

      const apiClientCall = method === 'post' ? apiClient.post : apiClient.put;
      await apiClientCall(apiUrl, payload);

      setLoading(false);
      setSuccess(isEditMode ? 'Product updated successfully!' : 'Product added successfully!');
      setTimeout(() => {
        navigate('/products'); // Navigate back to the list on success
      }, 2000);
    } catch (err) {
      console.error(isEditMode ? 'Error updating product:' : 'Error adding product:', err);
      setError(err.response?.data?.message || err.message || (isEditMode ? 'Failed to update product' : 'Failed to add product'));
      setLoading(false);
      setSuccess(null);
    }
  };

  const selectedCategoryData = categories.find(c => c._id === formData.category);

  const pageStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1400, // Higher than sidebar z-index (usually 1200-1300)
    backdropFilter: 'blur(2px)',
  };

  const formContainerStyle = {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '900px',
    marginTop: '20px',
    marginBottom: '20px',
  };

  const columnsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '20px',
  };

  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '20px',
  };

  const submitButtonStyles = {
    backgroundColor: isEditMode ? '#2196f3' : '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: isEditMode ? '#1976d2' : '#388e3c',
    },
    marginRight: '10px',
  };

  const cancelButtonStyles = {
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  };

  const sectionTitleStyle = {
    marginTop: '20px',
    marginBottom: '10px',
    borderBottom: '1px solid #ccc',
    paddingBottom: '5px',
    fontWeight: '600',
  };

  const imageContainerStyle = {
    position: 'relative',
    width: '100px', // Square dimension
    height: '100px', // Square dimension
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden', // Ensure image stays within bounds
  };

  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover', // Crop image to fit container
  };

  const deleteImageIconStyle = {
    position: 'absolute',
    top: '2px',
    right: '2px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '50%',
    padding: '2px',
  };

  return (
    <div style={pageStyle}>
      <div style={formContainerStyle}>
        <h3>{isEditMode ? 'Edit Product' : 'Add New Product'}</h3>
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Box component="form" sx={columnsContainerStyle}> {/* Changed form to Box and applied sx */}
          {/* First Column: Product Details */}
          <Box>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="brand-label">Brand</InputLabel>
              <Select
                labelId="brand-label"
                id="brand"
                name="brand"
                value={formData.brand || ''}
                onChange={handleInputChange}
                label="Brand"
              >
                {brands.map((brand) => (
                  <MenuItem key={brand._id} value={brand._id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
              {brandName && <small>Selected: {brandName}</small>}
            </FormControl>
            
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                id="category"
                name="category"
                value={formData.category || ''}
                onChange={handleInputChange}
                label="Category"
              >
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {categoryName && <small>Selected: {categoryName}</small>}
            </FormControl>
            {selectedCategoryData && selectedCategoryData.filters && selectedCategoryData.filters.length > 0 && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <div style={sectionTitleStyle}>Filters</div>
                <Grid container spacing={2}>
                  {selectedCategoryData.filters.map(filter => {
                    const selectedValue = formData.filters.find(f => f.key === filter.key);
                    return (
                      <Grid item xs={6} key={filter.key}>
                        <FormControl fullWidth margin="none">
                          <InputLabel>{filter.label}</InputLabel>
                          <Select
                            value={selectedValue?.valueId || ''}
                            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                            label={filter.label}
                          >
                            {filter.values.map(val => (
                              <MenuItem key={val._id} value={val._id}>{val.value}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Box>

          {/* Second Column: Filters, Images, Colors, Specifications */}
          <Box>
            <TextField
              label="Price"
              name="price"
              type="number"
              value={formData.price}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              required
              InputProps={{ inputProps: { min: 0 } }}
            />
            <TextField
              label="Discount Percentage"
              name="discountPercentage"
              type="number"
              value={formData.discountPercentage}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              InputProps={{ inputProps: { min: 0, max: 100 } }}
            />
            <TextField
              label="Description"
              name="description"
              multiline
              rows={2}
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Highlights (comma-separated)"
              name="highlights"
              multiline
              rows={2}
              value={formData.highlights}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Delivery Time"
              name="deliveryTime"
              value={formData.deliveryTime}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
            <FormControlLabel
              control={<Switch checked={formData.isFeatured} onChange={handleInputChange} name="isFeatured" />}
              label="Is Featured"
              margin="normal"
            />
            <TextField
              label="Stock"
              name="stock"
              type="number"
              value={formData.stock}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              required
              InputProps={{ inputProps: { min: 0 } }}
            />
            <TextField
              label="Content (Product Details)"
              name="content"
              multiline
              rows={4}
              value={formData.content}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
            <div style={sectionTitleStyle}>Images</div>
            <Grid container spacing={1}>
              {formData.images.map((image, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper elevation={1} style={imageContainerStyle}>
                    <img src={image} alt={`product-img-${index}`} style={imageStyle} />
                    <IconButton
                      aria-label="delete image"
                      onClick={() => handleDeleteImage(image)}
                      size="small"
                      style={deleteImageIconStyle}
                    >
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Box display="flex" alignItems="center" marginY={2}>
              <TextField
                label="Image URL"
                value={newImage}
                onChange={(e) => setNewImage(e.target.value)}
                size="small"
                style={{ marginRight: '10px', flexGrow: 1 }}
              />
              <Button variant="outlined" size="small" onClick={handleAddImage} startIcon={<AddIcon />}>
                Add
              </Button>
            </Box>

            <div style={sectionTitleStyle}>Colors</div>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: 2 }}>
              {formData.colors.map((color, index) => (
                <Chip
                  key={index}
                  label={color}
                  onDelete={() => handleDeleteColor(color)}
                  color="primary"
                />
              ))}
            </Box>
            <Box display="flex" alignItems="center" marginY={1}>
              <TextField
                label="Color Name (e.g., Red, #FF0000)"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                size="small"
                style={{ marginRight: '10px', flexGrow: 1 }}
              />
              <Button variant="outlined" size="small" onClick={handleAddColor} startIcon={<AddIcon />}>
                Add
              </Button>
            </Box>

            <div style={sectionTitleStyle}>Specifications</div>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: 2 }}>
              {formData.specifications && Object.entries(formData.specifications).map(([key, value]) => (
                 <Chip
                  key={key}
                  label={`${key}: ${value}`}
                  onDelete={() => handleDeleteSpecification(key)}
                />
              ))}

            </Box>
            <Box display="flex" alignItems="flex-start" gap={1} marginY={1}> {/* Changed alignItems */}
              <TextField
                label="Specification Key"
                value={newSpecificationKey}
                onChange={(e) => setNewSpecificationKey(e.target.value)}
                size="small"
                style={{ flexGrow: 1 }}
              />
              <TextField
                label="Specification Value"
                value={newSpecificationValue}
                onChange={(e) => setNewSpecificationValue(e.target.value)}
                size="small"
                style={{ flexGrow: 1 }}
              />
              <Button variant="outlined" size="small" onClick={handleAddSpecification} startIcon={<AddIcon />} sx={{ height: '40px' }}> {/* Adjusted height to match TextField */}
                Add
              </Button>
            </Box>
        </Box>
        <div style={buttonContainerStyle}>
          <Button
            variant="contained"
            sx={submitButtonStyles}
            onClick={handleSubmit}
            disabled={loading || (categories.length === 0 && !isEditMode)}
          >
            {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Product' : 'Add Product')}
          </Button>
          <Button variant="contained" sx={cancelButtonStyles} onClick={handleCancel}>
            Cancel
          </Button>
        </div>
        </Box>
      </div>
    </div>
  );
};

export default ProductForm;